import { useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, UserPlus, LogIn } from "lucide-react";
import { useCustomer } from "@/lib/customer";
import { formatApiErrorDetail } from "@/lib/auth";

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/[0.04] px-3.5 py-2.5 text-sm text-white outline-none placeholder:text-slate-500 focus:border-amber-300/40";

export default function CustomerAuth({ open, onClose, onAuthed, reason }) {
  const { register, login, loginWithGoogle } = useCustomer();
  const [tab, setTab] = useState("register");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const user =
        tab === "register"
          ? await register({ name: name.trim(), email: email.trim(), mobile: mobile.trim() || null, password })
          : await login(email.trim(), password);
      onAuthed?.(user);
      onClose?.();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            data-testid="customer-auth-modal"
            className="fixed inset-x-0 top-1/2 z-[61] mx-auto max-w-sm -translate-y-1/2 rounded-2xl border border-white/10 p-6"
            style={{ background: "rgba(10,14,23,0.98)" }}
            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
          >
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white" style={{ fontFamily: "'Syne', sans-serif" }}>
                {tab === "register" ? "Build My Journey" : "Welcome back"}
              </h2>
              <button data-testid="customer-auth-close-btn" onClick={onClose} className="grid h-8 w-8 place-items-center rounded-full border border-white/10 text-slate-300"><X size={15} /></button>
            </div>
            <p className="mt-1 text-xs text-slate-400">
              {reason || (tab === "register" ? "Create your free account to save and track your journey." : "Sign in to continue your journey.")}
            </p>

            {/* tab switch */}
            <div className="mt-4 grid grid-cols-2 gap-1 rounded-xl border border-white/10 bg-white/[0.03] p-1">
              <button
                data-testid="customer-auth-tab-register"
                onClick={() => { setTab("register"); setError(null); }}
                className="flex items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-semibold transition-colors"
                style={{ background: tab === "register" ? "rgba(226,194,117,0.15)" : "transparent", color: tab === "register" ? "#E2C275" : "#94A3B8" }}
              >
                <UserPlus size={13} /> Create account
              </button>
              <button
                data-testid="customer-auth-tab-login"
                onClick={() => { setTab("login"); setError(null); }}
                className="flex items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-semibold transition-colors"
                style={{ background: tab === "login" ? "rgba(226,194,117,0.15)" : "transparent", color: tab === "login" ? "#E2C275" : "#94A3B8" }}
              >
                <LogIn size={13} /> Sign in
              </button>
            </div>

            <form onSubmit={submit} className="mt-4 space-y-3">
              {tab === "register" && (
                <>
                  <input data-testid="customer-name-input" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className={inputCls} />
                  <input data-testid="customer-mobile-input" value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="Mobile (optional)" className={inputCls} />
                </>
              )}
              <input data-testid="customer-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className={inputCls} />
              <input data-testid="customer-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder={tab === "register" ? "Password (min 8 characters)" : "Password"} className={inputCls} />
              {error && <p data-testid="customer-auth-error" className="text-xs text-red-400">{error}</p>}
              <button data-testid="customer-auth-submit-btn" type="submit" disabled={loading}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-amber-300 py-2.5 text-sm font-semibold text-[#07090E] transition-transform active:scale-[0.98] disabled:opacity-60">
                {loading ? <Loader2 size={15} className="animate-spin" /> : null}
                {loading ? "Please wait…" : tab === "register" ? "Create account" : "Sign in"}
              </button>
            </form>

            <div className="my-4 flex items-center gap-3">
              <div className="h-px flex-1 bg-white/10" />
              <span className="text-[10px] uppercase tracking-wider text-slate-500">or</span>
              <div className="h-px flex-1 bg-white/10" />
            </div>

            <button
              data-testid="customer-google-btn"
              onClick={loginWithGoogle}
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-white/12 bg-white/[0.04] py-2.5 text-sm font-semibold text-white transition-colors hover:bg-white/[0.08]"
            >
              <GoogleIcon /> Continue with Google
            </button>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.2 35 26.7 36 24 36c-5.2 0-9.6-3.1-11.3-7.5l-6.5 5C9.6 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.1 5.5l6.3 5.3C42.9 35.8 44 30.3 44 24c0-1.3-.1-2.3-.4-3.5z" />
    </svg>
  );
}
